/**
 * React Query hook for header bootstrap data
 * 
 * Fetches all static/semi-static data needed for the header:
 * - User info (name, email, avatar, role, permissions)
 * - Company info (name, logo)
 * - Plan info
 * - Enabled features
 * - Sidebar visibility rules
 * 
 * This data is cached aggressively (30min stale, 60min cache) since it rarely changes.
 * The header should render immediately with placeholders while this loads.
 */

import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { apiClient } from '../api/apiClient';
import { queryOptions } from '../lib/queryClient';
import { normalizeCompanySlug } from '../utils/company';

export interface HeaderBootstrapData {
  user: {
    _id: string;
    fullName?: string;
    email?: string;
    phone?: string;
    role?: 'client' | 'admin' | 'manager' | 'agent' | 'superadmin';
    permissions?: string[];
    avatar?: string;
  };
  company: {
    name: string;
    logoUrl?: string | null;
  };
  plan: {
    name: string | null;
    features: string[] | null;
  };
  features: {
    enabled: string[] | null;
    hiddenFromSidebar: string[];
    locked: string[];
  };
}

/**
 * Hook to fetch header bootstrap data
 * 
 * @param company - Company slug (required)
 * @param options - Additional React Query options
 * @returns Query result with bootstrap data
 * 
 * @example
 * ```tsx
 * const { data, isLoading } = useHeaderBootstrap(company);
 * const userName = data?.user?.fullName || 'Loading...';
 * ```
 */
export function useHeaderBootstrap(
  company: string | undefined,
  options?: Omit<UseQueryOptions<HeaderBootstrapData>, 'queryKey' | 'queryFn'>
) {
  // Normalize company slug (e.g., "test2000" → "test" in development)
  const normalizedCompany = company ? normalizeCompanySlug(company) : undefined;
  
  // Determine account type: superadmin if company is 'superadmin', otherwise tenant
  const accountType: 'superadmin' | 'tenant' = normalizedCompany === 'superadmin' ? 'superadmin' : 'tenant';
  
  return useQuery<HeaderBootstrapData>({
    queryKey: ['header', 'bootstrap', normalizedCompany || '', accountType],
    queryFn: async () => {
      if (!normalizedCompany) {
        throw new Error('Company is required');
      }

      try {
        // CRITICAL: Always use normalized company slug in API calls
        const { data } = await apiClient.post<{ ok: boolean; data: HeaderBootstrapData }>(
          `/api/${normalizedCompany}/rpc/settings/bootstrap/header`,
          {},
          { 
            withCredentials: true,
            // Accept 404 as valid response (endpoint might not exist yet) - prevents axios from throwing
            validateStatus: (status) => status >= 200 && status < 500
          }
        );

        // Handle 404 gracefully - endpoint doesn't exist, use fallback
        if (!data || (data as any)?.status === 404) {
          // Log a single warning for 404 (not error spam)
          if (process.env.NODE_ENV === 'development') {
            console.warn('[useHeaderBootstrap] Endpoint not found (404), using fallback');
          }
          // Return minimal structure - components will handle missing data gracefully
          return {
            user: { _id: '', fullName: '', email: '', role: 'client' as const },
            company: { name: normalizedCompany },
            plan: { name: null, features: null },
            features: { enabled: null, hiddenFromSidebar: [], locked: [] },
          };
        }

        if (!data?.ok || !data?.data) {
          throw new Error((data as any)?.error || 'Failed to fetch header bootstrap');
        }

        return data.data;
      } catch (err: any) {
        // If endpoint doesn't exist yet, return fallback structure
        // This allows gradual migration
        if (err?.response?.status === 404) {
          // Log a single warning for 404 (not error spam)
          if (process.env.NODE_ENV === 'development') {
            console.warn('[useHeaderBootstrap] Endpoint not found (404), using fallback');
          }
          // Return minimal structure - components will handle missing data gracefully
          return {
            user: { _id: '', fullName: '', email: '', role: 'client' as const },
            company: { name: normalizedCompany },
            plan: { name: null, features: null },
            features: { enabled: null, hiddenFromSidebar: [], locked: [] },
          };
        }
        throw err;
      }
    },
    enabled: !!normalizedCompany,
    // Don't retry on 404 errors (company doesn't exist or endpoint not available)
    retry: (failureCount, error: any) => {
      const status = error?.response?.status ?? error?.status;
      // Don't retry on 404 - company doesn't exist or endpoint not available
      if (status === 404) return false;
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
    ...queryOptions.config, // 30min stale, 4h cache - config data
    ...options,
  });
}

